
import React from "react";
import { Link } from "react-router-dom";

const AboutHikmat = () => {
    return (
        <div className="min-h-[100dvh] bg-[#f7f5ef] text-stone-900">

            <div className="flex min-h-[100dvh] flex-col">

                {/* Header */}

                <header className="flex h-16 shrink-0 items-center justify-between border-b border-stone-900/5 px-5 sm:h-[72px] sm:px-8 lg:px-12">

                    <Link
                        to="/"
                        className="flex items-center gap-3 transition-opacity hover:opacity-70"
                    >
                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-900/15 font-serif text-lg">
                            H
                        </div>

                        <div>
                            <div className="font-serif text-xl leading-none tracking-tight">
                                Hikmah
                            </div>

                            <div className="mt-1 hidden text-[10px] uppercase tracking-[0.22em] text-stone-500 sm:block">
                                Wisdom from the adages of Imam Ali
                            </div>
                        </div>
                    </Link>

                    <Link
                        to="/"
                        className="rounded-full border border-stone-900/10 bg-white/60 px-4 py-2 text-xs font-medium text-stone-600 transition hover:border-stone-900/20 hover:bg-white hover:text-stone-900 sm:text-sm"
                    >
                        Back to Hikmah
                    </Link>

                </header>


                {/* Main content */}

                <main className="flex-1 px-5 py-12 sm:px-8 sm:py-20">

                    <div className="mx-auto max-w-2xl">

                        {/* Introduction */}

                        <section className="pb-14 sm:pb-16">

                            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.25em] text-stone-500">
                                About Hikmah
                            </p>

                            <h1 className="font-serif text-4xl leading-[1.15] tracking-tight text-stone-900 sm:text-5xl">
                                Wisdom was never meant
                                <br className="hidden sm:block" />
                                {" "}to remain on a page.
                            </h1>

                            <p className="mt-7 text-[15px] leading-8 text-stone-600 sm:text-base">
                                Across centuries, people have struggled
                                with remarkably similar questions —
                                about relationships, anger, ambition,
                                disappointment, justice, character,
                                and the choices that shape their lives.
                            </p>

                            <p className="mt-5 text-[15px] leading-8 text-stone-600 sm:text-base">
                                The circumstances may have changed,
                                but much of human nature has not.
                                And the wisdom left behind by those
                                who understood it deeply continues
                                to deserve our attention.
                            </p>

                            <p className="mt-6 font-serif text-xl leading-relaxed text-stone-900 sm:text-2xl">
                                Hikmah is an attempt to bring that
                                wisdom into everyday conversation.
                            </p>

                        </section>


                        {/* Origin */}

                        <section className="border-t border-stone-900/10 py-10">

                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                The beginning
                            </p>

                            <h2 className="font-serif text-2xl tracking-tight text-stone-900 sm:text-[28px]">
                                It began with a book.
                            </h2>

                            <p className="mt-5 text-[15px] leading-8 text-stone-600">
                                While reading{" "}
                                <em>2500 Adages of Imam Ali (a.s.)</em>,
                                we encountered a remarkable collection
                                of observations on human nature,
                                morality, relationships, wisdom,
                                and the conduct of life.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                Many of these sayings were brief,
                                yet carried ideas worth thinking
                                about long after reading them.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                But reading also raised a question:
                                how could someone facing a real
                                problem today discover the teachings
                                most relevant to their situation,
                                understand their meaning, and
                                consider how to apply them?
                            </p>

                            <p className="mt-5 font-serif text-xl leading-relaxed text-stone-900">
                                That question became the starting
                                point for Hikmah.
                            </p>

                        </section>


                        {/* How it works */}

                        <section className="border-t border-stone-900/10 py-10">

                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                The experience
                            </p>

                            <h2 className="font-serif text-2xl tracking-tight text-stone-900 sm:text-[28px]">
                                A conversation, not a quotation search.
                            </h2>

                            <p className="mt-5 text-[15px] leading-8 text-stone-600">
                                Hikmah allows you to describe a
                                situation in your own words.
                                It might be a disagreement with
                                someone, a difficult decision,
                                an emotional struggle, or simply
                                a question you've been considering.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                Behind the conversation, AI helps
                                search the collected teachings
                                of Imam Ali (a.s.) for ideas
                                relevant to what you've shared.
                                It then brings those teachings
                                together with reasoned explanations
                                and practical perspectives.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                You can ask follow-up questions,
                                explore different aspects of the
                                situation, or continue reflecting
                                on an idea that resonates with you.
                            </p>

                            <div className="mt-7 border-l-2 border-stone-900/20 pl-5">

                                <p className="font-serif text-xl leading-relaxed text-stone-800">
                                    The purpose is not to tell
                                    you what to think, but to
                                    offer something meaningful
                                    to think with.
                                </p>

                            </div>

                        </section>


                        {/* Growing library */}

                        <section className="border-t border-stone-900/10 py-10">

                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                Looking ahead
                            </p>

                            <h2 className="font-serif text-2xl tracking-tight text-stone-900 sm:text-[28px]">
                                A growing library of wisdom.
                            </h2>

                            <p className="mt-5 text-[15px] leading-8 text-stone-600">
                                Hikmah begins with{" "}
                                <em>2500 Adages of Imam Ali (a.s.)</em>,
                                but the vision extends beyond
                                a single collection.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                Over time, we hope to include
                                additional works, carefully
                                digitised and organised so
                                their teachings can be explored
                                through natural conversation.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                Each work will remain identifiable
                                as its own source of knowledge.
                                Preserving the distinction between
                                the original text and its modern
                                interpretation is an important
                                part of what we're building.
                            </p>

                        </section>


                        {/* Support */}

                        <section className="border-t border-stone-900/10 py-10">

                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                                Be part of it
                            </p>

                            <h2 className="font-serif text-2xl tracking-tight text-stone-900 sm:text-[28px]">
                                Help Hikmah grow.
                            </h2>

                            <p className="mt-5 text-[15px] leading-8 text-stone-600">
                                Hikmah is being developed with
                                the intention of making valuable
                                wisdom freely accessible to everyone.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                Building and maintaining it involves
                                ongoing work and expenses — from
                                preparing source material and
                                maintaining the platform to the
                                AI models that power each
                                conversation.
                            </p>

                            <p className="mt-4 text-[15px] leading-8 text-stone-600">
                                If you'd like to support its
                                development, contribute towards
                                running costs, help bring another
                                valuable book into the collection,
                                or suggest a work worth including,
                                we'd be glad to hear from you.
                            </p>

                            <a
                                href="mailto:hussain.haideri@softronikx.com?subject=Hikmah%20-%20Getting%20in%20Touch"
                                className="mt-7 inline-flex items-center gap-2 rounded-full border border-stone-900/15 bg-white/60 px-5 py-3 text-sm font-medium text-stone-700 transition hover:border-stone-900/30 hover:bg-white"
                            >
                                Get in touch

                                <span aria-hidden="true">
                                    ↗
                                </span>
                            </a>

                        </section>


                        {/* Return to conversation */}

                        <div className="pb-8 pt-4">

                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 font-medium text-stone-800 transition hover:text-stone-500"
                            >
                                <span>
                                    Explore Hikmah
                                </span>

                                <span aria-hidden="true">
                                    →
                                </span>
                            </Link>

                        </div>

                    </div>

                </main>


                {/* Footer */}

                <footer className="border-t border-stone-900/5 px-5 py-6 sm:px-8">

                    <div className="mx-auto flex max-w-2xl flex-col items-center justify-between gap-2 text-center text-[11px] text-stone-400 sm:flex-row sm:text-left">

                        <span>
                            Hikmah — Wisdom for everyday life
                        </span>

                        <a
                            href="https://www.softronikx.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition hover:text-stone-600"
                        >
                            Softronikx Technologies
                        </a>

                    </div>

                </footer>

            </div>

        </div>
    );
};

export default AboutHikmat;
